import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Alert,
  TablePagination,
  Stack,
  useTheme,
  Button,
  Snackbar,
  Chip,
  Checkbox,
  ListItemText,
  OutlinedInput,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Event,
  Refresh,
  Search,
  Visibility,
  CalendarMonth,
  Person,
  Assignment,
  Block,
  FilterList,
  CheckCircle,
  Warning,
  Add,
  Close,
  TableRows,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { useAuthToken } from '../TokenContext';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import axios from 'axios';

const MeetingSummariesTable = ({ collapsed }) => {
  const intl = useIntl();
  const theme = useTheme();
  const [meetings, setMeetings] = useState([]);
  const [filteredMeetings, setFilteredMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [timeRange, setTimeRange] = useState('all');
  const [selectedCandidate, setSelectedCandidate] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [contentFilter, setContentFilter] = useState('all');
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const token = useAuthToken();

  // New states for add meeting dialog
  const [addMeetingDialog, setAddMeetingDialog] = useState(false);
  const [postingMeeting, setPostingMeeting] = useState(false);
  const [postSuccess, setPostSuccess] = useState(false);
  const [pods, setPods] = useState([]);
  const [loadingPods, setLoadingPods] = useState(false);
  const [podEntries, setPodEntries] = useState({});
  const [podSearchQuery, setPodSearchQuery] = useState('');
  const [bulkUrl, setBulkUrl] = useState('');
  const [podPage, setPodPage] = useState(0);
  const [podRowsPerPage, setPodRowsPerPage] = useState(10);
  const [viewMode, setViewMode] = useState('timeline'); // 'timeline' or 'table'

  // Ensure pods is always an array
  const safePods = Array.isArray(pods) ? pods : [];

  // Filter pods based on search query
  const filteredPods = safePods.filter(pod => {
    if (!podSearchQuery.trim()) return true;

    const searchLower = podSearchQuery.toLowerCase();
    return (
      pod.pod_name?.toLowerCase().includes(searchLower) ||
      pod.pod_id?.toString().includes(searchLower) ||
      pod.art_name?.toLowerCase().includes(searchLower)
    );
  });

  // Get paginated pods
  const paginatedPods = filteredPods.slice(
    podPage * podRowsPerPage,
    podPage * podRowsPerPage + podRowsPerPage
  );

  const { user } = useSelector(state => state.auth);

  // Fetch pods from API
  const fetchPods = async () => {
    try {
      setLoadingPods(true);
      console.log('Making API call to fetch pods...');
      let apiUrl = process.env.REACT_APP_CLIENT_GETPODSFORMANAGER;
      let requestData = { emp_id: user.data[0].emp_id };

      if (user.data[0].type === 'Candidate') {
        apiUrl = process.env.REACT_APP_TALENT_PODLISTTALENT;
        requestData = { candidateID: user.data[0].c_id };
      }

      console.log('Request data:', requestData);
      console.log('Headers:', { Authorization: token });

      const response = await axios.post(
        apiUrl,
        requestData,
        { headers: { Authorization: token } }
      );

      const data = response.data;
      console.log('=== PODS API RESPONSE DEBUG ===');
      console.log('Full response:', response);
      console.log('Response status:', response.status);
      console.log('Response data:', data);
      console.log('Data type:', typeof data);
      console.log('Data keys:', Object.keys(data || {}));
      console.log('Is data array?', Array.isArray(data));

      if (data && data.body) {
        console.log('Body exists:', data.body);
        console.log('Body type:', typeof data.body);
        console.log('Is body array:', Array.isArray(data.body));
        console.log('Body length:', Array.isArray(data.body) ? data.body.length : 'N/A');
      }

      if (data && data.statusCode) {
        console.log('Status code in response:', data.statusCode);
      }

      // Try multiple possible response structures
      let podsData = [];

      // Structure 1: { statusCode: 200, body: [...] }
      if (data && data.body && Array.isArray(data.body)) {
        console.log('Using structure 1: data.body');
        podsData = data.body;
      }
      // Structure 2: { data: [...] }
      else if (data && data.data && Array.isArray(data.data)) {
        console.log('Using structure 2: data.data');
        podsData = data.data;
      }
      // Structure 3: Direct array
      else if (Array.isArray(data)) {
        console.log('Using structure 3: direct array');
        podsData = data;
      }
      // Structure 4: { pods: [...] }
      else if (data && data.pods && Array.isArray(data.pods)) {
        console.log('Using structure 4: data.pods');
        podsData = data.pods;
      }
      // Structure 5: { results: [...] }
      else if (data && data.results && Array.isArray(data.results)) {
        console.log('Using structure 5: data.results');
        podsData = data.results;
      }
      // Structure 6: Nested in response
      else if (data && data.response && Array.isArray(data.response)) {
        console.log('Using structure 6: data.response');
        podsData = data.response;
      }
      else {
        console.error('=== UNHANDLED RESPONSE STRUCTURE ===');
        console.error('Could not find pods array in any expected location');
        console.error('Response data:', data);
        console.error('Available keys:', Object.keys(data || {}));

        // Try to find any array in the response
        const findArrays = (obj, path = '') => {
          const arrays = [];
          if (Array.isArray(obj)) {
            arrays.push({ path: path || 'root', array: obj });
          } else if (obj && typeof obj === 'object') {
            Object.keys(obj).forEach(key => {
              const newPath = path ? `${path}.${key}` : key;
              arrays.push(...findArrays(obj[key], newPath));
            });
          }
          return arrays;
        };

        const foundArrays = findArrays(data);
        console.log('Found arrays in response:', foundArrays);

        if (foundArrays.length > 0) {
          console.log('Using first found array:', foundArrays[0].path);
          podsData = foundArrays[0].array;
        } else {
          console.error('No arrays found in response structure');
          podsData = [];
        }
      }

      console.log('Final pods data:', podsData);
      console.log('Final pods count:', Array.isArray(podsData) ? podsData.length : 0);
      console.log('=== END DEBUG ===');

      // Ensure we have a valid array
      if (!Array.isArray(podsData)) {
        console.warn('Final podsData is not an array, converting to empty array');
        podsData = [];
      }

      setPods(podsData);

      // Initialize pod entries when pods are loaded
      if (podsData.length > 0) {
        const initialPodEntries = {};
        podsData.forEach(pod => {
          // Handle different possible pod ID field names
          const podId = pod.pod_id || pod.id || pod.podId;
          if (podId) {
            initialPodEntries[podId] = {
              checked: false,
              url: ''
            };
          }
        });
        setPodEntries(initialPodEntries);
        console.log('Initialized pod entries for', Object.keys(initialPodEntries).length, 'pods');
      }

    } catch (err) {
      console.error('=== PODS API ERROR ===');
      console.error('Error details:', err);
      console.error('Error message:', err.message);
      console.error('Error response:', err.response);
      if (err.response) {
        console.error('Error response data:', err.response.data);
        console.error('Error response status:', err.response.status);
      }
      console.error('=== END ERROR DEBUG ===');

      setError('Failed to fetch pods: ' + err.message);
      setPods([]); // Ensure pods is always an array even on error
    } finally {
      setLoadingPods(false);
    }
  };

  // Open add meeting dialog and fetch pods
  const openAddMeetingDialog = () => {
    setAddMeetingDialog(true);
    fetchPods();
  };

  // Close dialog and reset form
  const closeAddMeetingDialog = () => {
    setAddMeetingDialog(false);
    setPodSearchQuery('');
    setBulkUrl('');
    setPodPage(0);
    // Reset all pod entries
    const resetPodEntries = {};
    safePods.forEach(pod => {
      resetPodEntries[pod.pod_id] = {
        checked: false,
        url: ''
      };
    });
    setPodEntries(resetPodEntries);
  };

  // Bulk operations
  const selectAllPods = () => {
    const updatedEntries = { ...podEntries };
    filteredPods.forEach(pod => {
      updatedEntries[pod.pod_id] = {
        ...updatedEntries[pod.pod_id],
        checked: true
      };
    });
    setPodEntries(updatedEntries);
  };

  const deselectAllPods = () => {
    const updatedEntries = { ...podEntries };
    filteredPods.forEach(pod => {
      updatedEntries[pod.pod_id] = {
        ...updatedEntries[pod.pod_id],
        checked: false
      };
    });
    setPodEntries(updatedEntries);
  };

  const applyBulkUrl = () => {
    if (!bulkUrl.trim()) return;

    const updatedEntries = { ...podEntries };
    Object.keys(updatedEntries).forEach(podId => {
      if (updatedEntries[podId].checked) {
        updatedEntries[podId].url = bulkUrl.trim();
      }
    });
    setPodEntries(updatedEntries);
  };

  // Pagination handlers
  const handlePodPageChange = (event, newPage) => {
    setPodPage(newPage);
  };

  const handlePodRowsPerPageChange = (event) => {
    setPodRowsPerPage(parseInt(event.target.value, 10));
    setPodPage(0);
  };

  // Update pod entry (checkbox or URL)
  const updatePodEntry = (podId, field, value) => {
    setPodEntries(prev => ({
      ...prev,
      [podId]: {
        ...prev[podId],
        [field]: value
      }
    }));
  };

  // Validate pod entries - at least one pod checked with URL
  const validatePodEntries = () => {
    return Object.values(podEntries).some(entry =>
      entry.checked && entry.url.trim() !== ''
    );
  };

  // Get checked pods with their URLs
  const getCheckedPodsData = () => {
    const checkedPods = [];
    Object.entries(podEntries).forEach(([podId, entry]) => {
      if (entry.checked && entry.url.trim()) {
        checkedPods.push({
          podId: parseInt(podId),
          url: entry.url.trim()
        });
      }
    });
    return checkedPods;
  };

  const postMeetingUrl = async () => {
    if (!validatePodEntries()) return;

    try {
      setPostingMeeting(true);
      setPostSuccess(false);
      setError(null);

      // Get checked pods data
      const checkedPodsData = getCheckedPodsData();

      // Build payload arrays - maintain order correlation
      const pod_ids = checkedPodsData.map(item => item.podId);
      const meeting_urls = checkedPodsData.map(item => item.url);

      const payload = {
        pod_ids,
        meeting_urls
      };

      console.log('Posting meetings with payload:', payload);
      console.log('Checked pods data:', checkedPodsData);

      const response = await fetch(process.env.REACT_APP_CLIENT_RECLLAI, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(`Failed to post meeting URLs: ${response.status}`);
      }

      const totalPods = pod_ids.length;
      const uniqueUrls = [...new Set(meeting_urls)].length;

      setPostSuccess({
        message: `Successfully added ${uniqueUrls} unique URL(s) to ${totalPods} pod(s)!`,
      });

      closeAddMeetingDialog();

      setTimeout(() => setPostSuccess(false), 5000);

    } catch (err) {
      setError(err.message);
    } finally {
      setPostingMeeting(false);
    }
  };

  const fetchMeetings = async () => {
    try {
      setLoading(true);
      const requestBody = user.data[0].type === 'Candidate'
        ? { manager_id: null, talent_id: user.data[0].c_id }
        : { manager_id: user.data[0].emp_id || null };

      const response = await fetch('https://7549jb05eg.execute-api.us-east-1.amazonaws.com/prod/meetingSync', {
        body: JSON.stringify(requestBody),
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(response.status === 401 ? 'Unauthorized: Please login again' : 'Failed to fetch meetings');
      }

      const data = await response.json();
      console.log('API Response:', data); // Debug log

      // Handle different possible response structures
      let meetingsArray = [];

      if (data && data.data && Array.isArray(data.data)) {
        // New structure: { status: 200, data: [...] }
        meetingsArray = data.data;
      } else if (data && data.body && data.body.data && data.body.data.transcripts) {
        // Old structure: { body: { data: { transcripts: [...] } } }
        meetingsArray = data.body.data.transcripts;
      } else if (Array.isArray(data)) {
        // Direct array
        meetingsArray = data;
      } else {
        console.error('Unexpected response structure:', data);
        throw new Error('Unexpected response structure from server');
      }

      // Filter and sort meetings
      const validMeetings = meetingsArray
        .filter(meeting => {
          // For new structure
          if (meeting.today_focus || meeting.accomplishments) return true;
          // For old structure
          if (meeting.summary && meeting.summary.short_summary) return true;
          return false;
        })
        .sort((a, b) => {
          // Use createdAt for new structure, dateString for old structure
          const dateA = new Date(a.createdAt || a.dateString);
          const dateB = new Date(b.createdAt || b.dateString);
          return dateB - dateA;
        });

      setMeetings(validMeetings);
      setFilteredMeetings(validMeetings);
    } catch (err) {
      console.error('Fetch meetings error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeetings();
  }, []);

  // Debug effect to track pods state changes
  useEffect(() => {
    console.log('Pods state changed:', pods);
    console.log('Pods type:', typeof pods);
    console.log('Is pods array:', Array.isArray(pods));
    console.log('Pods length:', Array.isArray(pods) ? pods.length : 'N/A');
  }, [pods]);

  useEffect(() => {
    filterMeetings();
  }, [searchQuery, startDate, endDate, timeRange, selectedCandidate, statusFilter, contentFilter, meetings]);

  const filterMeetings = () => {
    let filtered = [...meetings];

    // Text search filter
    if (searchQuery) {
      filtered = filtered.filter(meeting => {
        // Handle new structure
        if (meeting.today_focus || meeting.accomplishments || meeting.pod_candidates || meeting.blockers) {
          return (
            meeting.today_focus?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            meeting.accomplishments?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            meeting.pod_candidates?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            meeting.blockers?.toLowerCase().includes(searchQuery.toLowerCase())
          );
        }
        // Handle old structure
        if (meeting.summary && meeting.summary.short_summary) {
          return meeting.summary.short_summary.toLowerCase().includes(searchQuery.toLowerCase());
        }
        return false;
      });
    }

    // Pod Candidates filter
    if (selectedCandidate !== 'all') {
      filtered = filtered.filter(meeting =>
        meeting.pod_candidates?.toLowerCase().includes(selectedCandidate.toLowerCase())
      );
    }

    // Status filter (based on blockers)
    if (statusFilter !== 'all') {
      filtered = filtered.filter(meeting => {
        if (statusFilter === 'blocked') {
          return meeting.blockers && meeting.blockers.trim() !== '';
        } else if (statusFilter === 'unblocked') {
          return !meeting.blockers || meeting.blockers.trim() === '';
        }
        return true;
      });
    }

    // Content completeness filter
    if (contentFilter !== 'all') {
      filtered = filtered.filter(meeting => {
        if (contentFilter === 'complete') {
          return meeting.today_focus && meeting.accomplishments &&
            meeting.today_focus.trim() !== '' && meeting.accomplishments.trim() !== '';
        } else if (contentFilter === 'incomplete') {
          return !meeting.today_focus || !meeting.accomplishments ||
            meeting.today_focus.trim() === '' || meeting.accomplishments.trim() === '';
        }
        return true;
      });
    }

    // Date filters
    if (startDate) {
      filtered = filtered.filter(meeting => {
        const meetingDate = new Date(meeting.createdAt || meeting.dateString);
        return meetingDate >= new Date(startDate);
      });
    }

    if (endDate) {
      filtered = filtered.filter(meeting => {
        const meetingDate = new Date(meeting.createdAt || meeting.dateString);
        return meetingDate <= new Date(endDate);
      });
    }

    // Time range filter
    if (timeRange !== 'all') {
      const now = new Date();
      let startDate = new Date();
      let endDate = new Date();

      switch (timeRange) {
        case 'week':
          startDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          endDate = new Date(now.getFullYear(), now.getMonth(), 0);
          break;
        case '3months':
          startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1);
          endDate = new Date(now.getFullYear(), now.getMonth(), 0);
          break;
        default:
          break;
      }

      filtered = filtered.filter(meeting => {
        const meetingDate = new Date(meeting.createdAt || meeting.dateString);
        meetingDate.setHours(0, 0, 0, 0);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        return meetingDate >= startDate && meetingDate <= endDate;
      });
    }

    setFilteredMeetings(filtered);
    setPage(0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get unique candidates for filter dropdown
  const getUniqueCandidates = () => {
    const candidates = meetings
      .map(meeting => meeting.pod_candidates)
      .filter(candidate => candidate && candidate.trim() !== '')
      .map(candidate => candidate.trim());

    return [...new Set(candidates)].sort();
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedCandidate('all');
    setStatusFilter('all');
    setContentFilter('all');
    setTimeRange('all');
    setStartDate('');
    setEndDate('');
  };

  // Combine today's focus and accomplishments into one summary
  const getCombinedSummary = (meeting) => {
    let summary = '';

    // For new structure
    if (meeting.today_focus || meeting.accomplishments) {
      if (meeting.today_focus && meeting.today_focus.trim()) {
        summary += meeting.today_focus.trim();
      }
      if (meeting.accomplishments && meeting.accomplishments.trim()) {
        if (summary) summary += ' ';
        summary += meeting.accomplishments.trim();
      }
    }

    // For old structure
    if (!summary && meeting.summary?.short_summary) {
      summary = meeting.summary.short_summary;
    }

    return summary || 'No summary available';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Container sx={{
      py: 1,
      width: '100%',
      maxWidth: 'none',
      marginLeft: 0,
      marginRight: 0,
      transition: 'all 0.3s ease',
    }}>
      <Paper
        elevation={0}
        sx={{
          p: 0,
          backgroundColor: 'transparent',
          borderRadius: 2
        }}
      >
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              color: 'var(--artemy-text-primary)',
              fontWeight: 700,
              fontFamily: 'var(--artemy-font-display)',
              fontSize: '20px !important',
              letterSpacing: '-0.3px'
            }}
          >
            {intl.formatMessage({ id: 'podSyncSummaries' })}
          </Typography>

          <Box display="flex" alignItems="center" gap={2}>
            <ToggleButtonGroup
              size="small"
              value={viewMode}
              exclusive
              onChange={(e, next) => next && setViewMode(next)}
              sx={{
                '& .MuiToggleButton-root': {
                  color: 'var(--artemy-text-dim)',
                  borderColor: 'var(--artemy-border)',
                  px: 1.5,
                  py: 0.5,
                  '&.Mui-selected': {
                    color: 'var(--artemy-accent)',
                    backgroundColor: 'rgba(61, 127, 255, 0.1)',
                  }
                }
              }}
            >
              <ToggleButton value="timeline">
                <TimelineIcon sx={{ fontSize: 18, mr: 0.5 }} />
                Timeline
              </ToggleButton>
              <ToggleButton value="table">
                <TableRows sx={{ fontSize: 18, mr: 0.5 }} />
                Table
              </ToggleButton>
            </ToggleButtonGroup>

            <Button
              className="artemy-btn artemy-btn-primary"
              startIcon={<Add />}
              onClick={openAddMeetingDialog}
              sx={{
                textTransform: 'none',
                height: '34px',
                borderRadius: '8px'
              }}
            >
              {intl.formatMessage({ id: 'addMeeting' })}
            </Button>
            <Tooltip title={intl.formatMessage({ id: 'refresh' })}>
              <IconButton
                onClick={fetchMeetings}
                disabled={loading}
                sx={{
                  color: 'var(--artemy-text-secondary)',
                  '&:hover': {
                    backgroundColor: 'var(--artemy-bg-elevated)',
                  }
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Filters Section */}
        <div 
          className="artemy-card"
          style={{
            padding: '16px 20px',
            marginBottom: 20,
            background: 'var(--artemy-bg-surface)',
            backdropFilter: 'blur(12px)',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder={intl.formatMessage({ id: 'searchInSummaries' })}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: 'var(--artemy-text-dim)', fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'var(--artemy-bg-elevated)',
                    height: '38px',
                    borderRadius: '8px',
                    '& fieldset': { borderColor: 'var(--artemy-border)' },
                    '&:hover fieldset': { borderColor: 'var(--artemy-border-bright)' },
                    '&.Mui-focused fieldset': { borderColor: 'var(--artemy-accent)' }
                  },
                  '& .MuiInputBase-input': { color: 'var(--artemy-text-primary)', fontSize: '13px' }
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>{intl.formatMessage({ id: 'quickTimeRange' })}</InputLabel>
                <Select
                  value={timeRange}
                  onChange={(e) => {
                    setTimeRange(e.target.value);
                    if (e.target.value !== 'all') {
                      setStartDate('');
                      setEndDate('');
                    }
                  }}
                  label={intl.formatMessage({ id: 'quickTimeRange' })}
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      }
                    }
                  }}
                >
                  <MenuItem value="all">{intl.formatMessage({ id: 'showAllTime' })}</MenuItem>
                  <MenuItem value="week">Last Week</MenuItem>
                  <MenuItem value="month">Last Month</MenuItem>
                  <MenuItem value="3months">Last 3 Months</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={2}>
                {['start', 'end'].map((type) => (
                  <TextField
                    key={type}
                    type="date"
                    label={`${type === 'start' ? intl.formatMessage({ id: 'startDate' }) : intl.formatMessage({ id: 'endDate' })}`}
                    value={type === 'start' ? startDate : endDate}
                    onChange={(e) => {
                      if (type === 'start') setStartDate(e.target.value);
                      else setEndDate(e.target.value);
                      setTimeRange('all');
                    }}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    disabled={timeRange !== 'all'}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: theme.palette.background.paper,
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.main,
                        }
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <CalendarMonth color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                ))}
              </Stack>
            </Grid>
          </Grid>
        </div>

        {/* Content Section */}
        {viewMode === 'timeline' ? (
          <Box className="artemy-fade-up" sx={{ mt: 2 }}>
            {filteredMeetings.length === 0 ? (
              <div className="artemy-card" style={{ padding: '40px', textAlign: 'center', color: 'var(--artemy-text-dim)' }}>
                No meetings found matching your criteria.
              </div>
            ) : (
              <Box 
                sx={{ 
                  position: 'relative',
                  pl: 4,
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    left: '15px',
                    top: 0,
                    bottom: 0,
                    width: '2px',
                    backgroundColor: 'var(--artemy-border)',
                    borderLeft: '2px dashed var(--artemy-border)',
                    opacity: 0.5
                  }
                }}
              >
                {filteredMeetings
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((meeting, index) => (
                    <Box 
                      key={index}
                      sx={{ 
                        position: 'relative',
                        mb: 4,
                        '&:last-child': { mb: 0 }
                      }}
                    >
                      {/* Timeline Dot */}
                      <Box 
                        sx={{ 
                          position: 'absolute',
                          left: '-25.5px',
                          top: '12px',
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          backgroundColor: 'var(--artemy-bg-base)',
                          border: '2px solid var(--artemy-accent)',
                          boxShadow: '0 0 10px rgba(61, 127, 255, 0.4)',
                          zIndex: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      />
                      
                      {/* Timeline Content */}
                      <div 
                        className="artemy-card"
                        style={{ 
                          padding: '16px 20px',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          background: 'var(--artemy-bg-surface)',
                          marginLeft: '12px'
                        }}
                        onClick={() => setSelectedMeeting(meeting)}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography sx={{ 
                            fontFamily: 'var(--artemy-font-mono)', 
                            fontSize: '12px', 
                            color: 'var(--artemy-accent)',
                            fontWeight: 500
                          }}>
                            {formatDate(meeting.createdAt || meeting.dateString)}
                          </Typography>
                          <Visibility sx={{ fontSize: 14, color: 'var(--artemy-text-dim)' }} />
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'var(--artemy-text-primary)',
                            lineHeight: 1.6,
                            fontSize: '13px',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {getCombinedSummary(meeting)}
                        </Typography>
                      </div>
                    </Box>
                  ))}
              </Box>
            )}
            
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredMeetings.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: '1px solid var(--artemy-border)',
                color: 'var(--artemy-text-primary)'
              }}
            />
          </Box>
        ) : (
          <div
            className="artemy-card"
            style={{
              width: '100%',
              overflow: 'hidden',
            }}
          >
            <Table stickyHeader sx={{ tableLayout: 'fixed' }}>
              <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: 'var(--artemy-bg-surface)',
                    color: 'var(--artemy-text-secondary)',
                    fontWeight: 500,
                    fontFamily: 'var(--artemy-font-mono)',
                    fontSize: '10px',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    width: '30%',
                    pr: 3,
                    borderBottom: '1px solid var(--artemy-border)'
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <Event sx={{ mr: 1, fontSize: 14 }} />
                    {intl.formatMessage({ id: 'dateAndTime' })}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: 'var(--artemy-bg-surface)',
                    color: 'var(--artemy-text-secondary)',
                    fontWeight: 500,
                    fontFamily: 'var(--artemy-font-mono)',
                    fontSize: '10px',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    width: '70%',
                    pl: 6,
                    borderBottom: '1px solid var(--artemy-border)'
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <Assignment sx={{ mr: 1, fontSize: 14 }} />
                    Summary
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMeetings
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((meeting, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      backgroundColor: theme.palette.mode === 'dark'
                        ? index % 2 === 0 ? 'rgba(255, 255, 255, 0.02)' : 'transparent'
                        : index % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark'
                          ? `${theme.palette.primary.dark}30`
                          : `${theme.palette.primary.light}30`,
                        transition: 'background-color 0.2s ease'
                      }
                    }}
                  >
                    <TableCell sx={{
                      color: 'var(--artemy-text-primary)',
                      width: '30%',
                      pr: 3,
                      borderBottom: '1px solid var(--artemy-border)',
                      fontFamily: 'var(--artemy-font-mono)',
                      fontSize: '12px',
                      wordWrap: 'break-word'
                    }}>
                      {formatDate(meeting.createdAt || meeting.dateString)}
                    </TableCell>
                    <TableCell sx={{
                      color: 'var(--artemy-text-primary)',
                      width: '70%',
                      pl: 6,
                      borderBottom: '1px solid var(--artemy-border)',
                      wordWrap: 'break-word'
                    }}>
                      <Tooltip title="View Summary">
                        <IconButton
                          onClick={() => setSelectedMeeting(meeting)}
                          sx={{
                            color: 'var(--artemy-accent)',
                            '&:hover': {
                              backgroundColor: 'rgba(61, 127, 255, 0.1)'
                            }
                          }}
                        >
                          <Visibility sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredMeetings.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: '1px solid var(--artemy-border)',
              backgroundColor: 'var(--artemy-bg-surface)',
              color: 'var(--artemy-text-primary)'
            }}
          />
        </div>
      )}

      {/* Add Meeting Dialog */}
        <Dialog
          open={addMeetingDialog}
          onClose={closeAddMeetingDialog}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px',
              backgroundColor: 'var(--artemy-bg-elevated)',
              backgroundImage: 'none',
              border: '1px solid var(--artemy-border)',
              backdropFilter: 'blur(20px)',
              height: '80vh',
              maxHeight: '800px'
            }
          }}
        >
          <DialogTitle sx={{
            borderBottom: '1px solid var(--artemy-border)',
            backgroundColor: 'var(--artemy-bg-surface)',
            color: 'var(--artemy-text-primary)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 2
          }}>
            <Box display="flex" alignItems="center">
              <Add sx={{ mr: 1, color: 'var(--artemy-accent)' }} />
              <Typography variant="h6" sx={{ fontFamily: 'var(--artemy-font-display)', fontWeight: 700, fontSize: '18px' }}>
                {intl.formatMessage({ id: 'addMeeting' })}
              </Typography>
            </Box>
            <IconButton
              onClick={closeAddMeetingDialog}
              sx={{ color: 'var(--artemy-text-dim)' }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ mt: 2, position: 'relative', minHeight: '400px' }}>
            {loadingPods ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(0, 0, 0, 0.7)'
                    : 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(2px)',
                  zIndex: 1000,
                  borderRadius: 1
                }}
              >
                <CircularProgress
                  size={40}
                  sx={{
                    color: theme.palette.primary.main,
                    mb: 2
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.text.primary,
                    mb: 1,
                    textAlign: 'center'
                  }}
                >
                  Loading Pods...
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    textAlign: 'center'
                  }}
                >
                  Please wait while we fetch your available pods
                </Typography>
              </Box>
            ) : null}

            <Box sx={{ opacity: loadingPods ? 0.3 : 1 }}>
              {/* Header with Search and Summary */}
              <Box mb={3}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder="Search pods by name, ID, or ART name..."
                      value={podSearchQuery}
                      onChange={(e) => {
                        setPodSearchQuery(e.target.value);
                        setPodPage(0); // Reset to first page when searching
                      }}
                      disabled={loadingPods}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'var(--artemy-bg-surface)',
                          height: '40px',
                          borderRadius: '8px',
                          '& fieldset': { borderColor: 'var(--artemy-border)' },
                          '&:hover fieldset': { borderColor: 'var(--artemy-border-bright)' },
                          '&.Mui-focused fieldset': { borderColor: 'var(--artemy-accent)' }
                        },
                        '& .MuiInputBase-input': { color: 'var(--artemy-text-primary)', fontSize: '13px' }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: podSearchQuery && (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => {
                                setPodSearchQuery('');
                                setPodPage(0);
                              }}
                              size="small"
                              sx={{ color: theme.palette.text.secondary }}
                            >
                              <Close fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <div style={{ 
                      padding: '8px 12px', 
                      borderRadius: '8px', 
                      background: 'rgba(61, 127, 255, 0.05)', 
                      border: '1px solid rgba(61, 127, 255, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Assignment sx={{ color: 'var(--artemy-accent)', fontSize: 16 }} />
                      <Typography variant="body2" sx={{ color: 'var(--artemy-text-primary)', fontWeight: 500, fontSize: '12px' }}>
                        {Object.values(podEntries).filter(entry => entry.checked).length} selected
                        {filteredPods.length !== safePods.length && (
                          <> • {filteredPods.length} shown</>
                        )}
                      </Typography>
                    </div>
                  </Grid>
                </Grid>
              </Box>

              {/* Bulk Actions */}
              <div 
                className="artemy-card"
                style={{
                  padding: '16px',
                  marginBottom: 20,
                  background: 'var(--artemy-bg-surface)',
                  borderStyle: 'dashed'
                }}
              >
                <Typography variant="subtitle2" sx={{ fontFamily: 'var(--artemy-font-mono)', fontSize: '11px', color: 'var(--artemy-text-secondary)', mb: 2, textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Bulk Actions
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={5}>
                    <Stack direction="row" spacing={1}>
                      <button
                        className="artemy-btn artemy-btn-ghost"
                        style={{ fontSize: '11px', padding: '5px 10px' }}
                        onClick={selectAllPods}
                        disabled={loadingPods || filteredPods.length === 0}
                      >
                        Select All ({filteredPods.length})
                      </button>
                      <button
                        className="artemy-btn artemy-btn-ghost"
                        style={{ fontSize: '11px', padding: '5px 10px' }}
                        onClick={deselectAllPods}
                        disabled={loadingPods}
                      >
                        Deselect All
                      </button>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <input
                        className="artemy-input"
                        style={{ height: '32px', fontSize: '12px' }}
                        placeholder="Enter URL for selected pods"
                        value={bulkUrl}
                        onChange={(e) => setBulkUrl(e.target.value)}
                        disabled={loadingPods}
                      />
                      <button
                        className="artemy-btn artemy-btn-primary"
                        style={{ height: '32px', fontSize: '11px' }}
                        onClick={applyBulkUrl}
                        disabled={loadingPods || !bulkUrl.trim() || Object.values(podEntries).filter(entry => entry.checked).length === 0}
                      >
                        Apply
                      </button>
                    </Stack>
                  </Grid>
                </Grid>
              </div>

              {/* Pods Table */}
              {filteredPods.length === 0 && !loadingPods ? (
                <Alert severity="warning">
                  <Typography>
                    {podSearchQuery.trim() ? 'No pods found matching your search' : 'No pods available'}
                  </Typography>
                </Alert>
              ) : (
                <Paper elevation={1}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: '60px', backgroundColor: 'var(--artemy-bg-elevated)', color: 'var(--artemy-text-secondary)', borderBottom: '1px solid var(--artemy-border)', fontFamily: 'var(--artemy-font-mono)', fontSize: '10px', textTransform: 'uppercase' }}>Select</TableCell>
                        <TableCell sx={{ backgroundColor: 'var(--artemy-bg-elevated)', color: 'var(--artemy-text-secondary)', borderBottom: '1px solid var(--artemy-border)', fontFamily: 'var(--artemy-font-mono)', fontSize: '10px', textTransform: 'uppercase' }}>Pod Details</TableCell>
                        <TableCell sx={{ width: '300px', backgroundColor: 'var(--artemy-bg-elevated)', color: 'var(--artemy-text-secondary)', borderBottom: '1px solid var(--artemy-border)', fontFamily: 'var(--artemy-font-mono)', fontSize: '10px', textTransform: 'uppercase' }}>Meeting URL</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedPods.map((pod) => (
                        <TableRow
                          key={pod.pod_id}
                          hover
                          sx={{
                            backgroundColor: podEntries[pod.pod_id]?.checked
                              ? 'rgba(61, 127, 255, 0.05)'
                              : 'transparent',
                            '&:hover': { background: 'rgba(255,255,255,0.02) !important' }
                          }}
                        >
                          <TableCell sx={{ borderBottom: '1px solid var(--artemy-border)' }}>
                            <Checkbox
                              checked={podEntries[pod.pod_id]?.checked || false}
                              onChange={(e) => updatePodEntry(pod.pod_id, 'checked', e.target.checked)}
                              disabled={loadingPods}
                              sx={{
                                color: 'var(--artemy-border-bright)',
                                '&.Mui-checked': {
                                  color: 'var(--artemy-accent)',
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ borderBottom: '1px solid var(--artemy-border)' }}>
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 600,
                                  fontFamily: 'var(--artemy-font-body)',
                                  color: podEntries[pod.pod_id]?.checked
                                    ? 'var(--artemy-accent)'
                                    : 'var(--artemy-text-primary)',
                                  fontSize: '13px'
                                }}
                              >
                                {pod.pod_name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'var(--artemy-text-dim)', fontFamily: 'var(--artemy-font-mono)', fontSize: '10px' }}>
                                ID: {pod.pod_id} • {pod.art_name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderBottom: '1px solid var(--artemy-border)' }}>
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="Enter meeting URL"
                              value={podEntries[pod.pod_id]?.url || ''}
                              onChange={(e) => updatePodEntry(pod.pod_id, 'url', e.target.value)}
                              disabled={loadingPods || !podEntries[pod.pod_id]?.checked}
                              error={
                                podEntries[pod.pod_id]?.checked &&
                                (!podEntries[pod.pod_id]?.url || podEntries[pod.pod_id]?.url.trim() === '')
                              }
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: podEntries[pod.pod_id]?.checked
                                    ? 'var(--artemy-bg-surface)'
                                    : 'transparent',
                                  height: '32px',
                                  fontSize: '12px',
                                  borderRadius: '6px',
                                  '& fieldset': { borderColor: 'var(--artemy-border)' },
                                  '&:hover fieldset': { borderColor: 'var(--artemy-border-bright)' },
                                  '&.Mui-focused fieldset': { borderColor: 'var(--artemy-accent)' }
                                },
                                '& .MuiInputBase-input': { color: 'var(--artemy-text-primary)' }
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Pagination */}
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={filteredPods.length}
                    rowsPerPage={podRowsPerPage}
                    page={podPage}
                    onPageChange={handlePodPageChange}
                    onRowsPerPageChange={handlePodRowsPerPageChange}
                    sx={{
                      borderTop: `1px solid ${theme.palette.divider}`,
                    }}
                  />
                </Paper>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid var(--artemy-border)' }}>
            <button
              className="artemy-btn artemy-btn-ghost"
              onClick={closeAddMeetingDialog}
              disabled={postingMeeting}
            >
              Cancel
            </button>
            <button
              className="artemy-btn artemy-btn-primary"
              onClick={postMeetingUrl}
              disabled={postingMeeting || !validatePodEntries() || loadingPods}
            >
              {postingMeeting ? (
                <>
                  <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                  Adding Meetings...
                </>
              ) : loadingPods ? (
                'Loading Pods...'
              ) : (
                `Add to ${Object.values(podEntries).filter(entry => entry.checked).length} Pod${Object.values(podEntries).filter(entry => entry.checked).length !== 1 ? 's' : ''}`
              )}
            </button>
          </DialogActions>
        </Dialog>

        {/* Summary Dialog */}
        <Dialog
          open={Boolean(selectedMeeting)}
          onClose={() => setSelectedMeeting(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px',
              backgroundColor: 'var(--artemy-bg-elevated)',
              backgroundImage: 'none',
              border: '1px solid var(--artemy-border)',
              backdropFilter: 'blur(20px)',
            }
          }}
        >
          <DialogTitle sx={{
            borderBottom: '1px solid var(--artemy-border)',
            backgroundColor: 'var(--artemy-bg-surface)',
            color: 'var(--artemy-text-primary)',
            py: 2
          }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center">
                <Assignment sx={{ mr: 1, color: 'var(--artemy-accent)' }} />
                <Typography variant="h6" sx={{ fontFamily: 'var(--artemy-font-display)', fontWeight: 700, fontSize: '18px' }}>
                  Meeting Summary
                </Typography>
              </Box>
              <IconButton onClick={() => setSelectedMeeting(null)} sx={{ color: 'var(--artemy-text-dim)' }}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 4, mt: 1 }}>
            {selectedMeeting && (
              <Typography variant="body1" sx={{
                color: 'var(--artemy-text-primary)',
                lineHeight: 1.8,
                fontSize: '14px',
                fontFamily: 'var(--artemy-font-body)'
              }}>
                {getCombinedSummary(selectedMeeting)}
              </Typography>
            )}
          </DialogContent>
        </Dialog>
      </Paper>

      {/* Success Snackbar */}
      <Snackbar
        open={Boolean(postSuccess)}
        autoHideDuration={5000}
        onClose={() => setPostSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setPostSuccess(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {typeof postSuccess === 'object' ? postSuccess.message : 'Meeting URL posted successfully!'}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MeetingSummariesTable;